import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Amplify } from 'aws-amplify';
import {
  confirmSignIn,
  fetchAuthSession,
  signIn,
  signOut,
} from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { sessionStorage as amplifySessionStorage } from 'aws-amplify/utils';
import type { User } from '../types';
import { api } from '../services/api';

// ── Cognito pool configuration ────────────────────────────────────────────────
// Amplify JS v6 (`aws-amplify/auth`). Migrated from `amazon-cognito-identity-js`,
// which is part of the Amplify v5 ecosystem: maintenance mode 2026-09-01,
// end of support 2027-03-01 (AWS Health AWS_AMPLIFY_CONSOLE_PLANNED_LIFECYCLE_EVENT).
// Auth flow is unchanged — v6 `signIn` uses USER_SRP_AUTH by default, which the
// `iad2-prototype-client` app client allows (and it carries no client secret).

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId:       process.env.REACT_APP_COGNITO_POOL_ID   || 'us-east-1_ZwstQBfM0',
      userPoolClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || 'b87ph5ht6fb8v1o07p3t8otn6',
    },
  },
});

// Keep Amplify's own token cache in sessionStorage (its default is localStorage).
// This preserves the "tokens die with the tab" posture described below, and is
// stricter than the previous behaviour — amazon-cognito-identity-js kept its
// CognitoUserPool tokens in localStorage regardless of our sessionStorage copy.
cognitoUserPoolsTokenProvider.setKeyValueStorage(amplifySessionStorage);

const API_URL =
  process.env.REACT_APP_API_URL ||
  'https://yvui056ywi.execute-api.us-east-1.amazonaws.com/prototype';

// ── Storage keys — sessionStorage only (cleared when browser tab closes) ──────
// Tokens are NOT stored in localStorage. The primary auth mechanism is the
// HttpOnly cookie set by POST /auth/session. sessionStorage is kept as a
// fallback so the Authorization header can still be sent to API Gateway's
// Cognito authorizer. Full cookie-only auth requires a custom Lambda
// authorizer (recommended before production go-live — see docs/runbook.md).

const SESSION_TOKEN_KEY = 'iad2_token';
const SESSION_USER_KEY  = 'iad2_user';

// ── JWT payload parser ────────────────────────────────────────────────────────

function parseJwtClaims(token: string): Record<string, unknown> {
  try {
    const base64    = token.split('.')[1];
    const padded    = base64.replace(/-/g, '+').replace(/_/g, '/');
    const padLength = (4 - (padded.length % 4)) % 4;
    const decoded   = atob(padded + '='.repeat(padLength));
    return JSON.parse(decoded) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function buildUserFromClaims(claims: Record<string, unknown>, idToken: string): User {
  const sub    = (claims['sub']         as string) ?? '';
  const email  = (claims['email']       as string) ?? '';
  const given  = (claims['given_name']  as string) ?? '';
  const family = (claims['family_name'] as string) ?? '';

  const roleAttr =
    (claims['custom:role'] as string) ??
    (claims['cognito:groups'] as string[])?.[0] ??
    'User';

  const normaliseRole = (r: string): User['role'] => {
    if (/admin/i.test(r))  return 'Administrator';
    if (/super/i.test(r))  return 'Superuser';
    return 'User';
  };

  const role             = normaliseRole(roleAttr);
  const roleId           = role === 'Administrator' ? 1 : role === 'Superuser' ? 2 : 3;
  // Clearance is the claim, and ONLY the claim. Treating Administrator/Superuser as
  // implying it contradicts the model the backend enforces -- can_see_restricted is
  // orthogonal to role, and an administrator without clearance is a deliberate state. The
  // backend ANDs against the real claim regardless, so this never granted data; what it did
  // was show restricted-data controls to users whose requests are then refused, which reads
  // as the app being broken and invites someone to "fix" it in the wrong direction.
  const canSeeRestricted = (claims['custom:can_see_restricted'] as string) === 'true';

  // Store in sessionStorage — clears when the browser tab closes
  sessionStorage.setItem(SESSION_TOKEN_KEY, idToken);

  const user: User = { id: sub, first_name: given, last_name: family, email, role, roleId, canSeeRestricted, cognitoSub: sub };
  sessionStorage.setItem(SESSION_USER_KEY, JSON.stringify(user));

  // Set HttpOnly cookie via backend for defense-in-depth
  setSessionCookie(idToken).catch(console.warn);

  return user;
}

async function setSessionCookie(token: string): Promise<void> {
  try {
    await fetch(`${API_URL}/auth/session`, {
      method:      'POST',
      credentials: 'include',
      headers:     { 'Content-Type': 'application/json' },
      body:        JSON.stringify({ token }),
    });
  } catch {
    // Non-fatal — sessionStorage fallback remains active
  }
}

async function clearSessionCookie(): Promise<void> {
  try {
    await fetch(`${API_URL}/auth/session`, {
      method:      'DELETE',
      credentials: 'include',
    });
  } catch {
    // Non-fatal
  }
}

// ── Login result ──────────────────────────────────────────────────────────────

export type LoginResult =
  | { type: 'success' }
  | { type: 'new_password_required'; complete: (newPassword: string) => Promise<void> };

// ── Context shape ─────────────────────────────────────────────────────────────

interface AuthContextValue {
  user:      User | null;
  isLoading: boolean;
  login:     (email: string, password: string) => Promise<LoginResult>;
  logout:    () => void;
  isRole:    (role: User['role']) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ── Provider ──────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user,      setUser]      = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const restoreSession = async () => {
      try {
        const storedToken = sessionStorage.getItem(SESSION_TOKEN_KEY);
        const storedUser  = sessionStorage.getItem(SESSION_USER_KEY);

        if (storedToken && storedUser) {
          const claims  = parseJwtClaims(storedToken);
          const exp     = (claims['exp'] as number) ?? 0;
          const nowSec  = Math.floor(Date.now() / 1000);

          if (exp > nowSec) {
            setUser(JSON.parse(storedUser) as User);
            setIsLoading(false);
            return;
          }
        }

        // No usable cached session — ask Amplify, which refreshes the ID token
        // from the refresh token when one is present.
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();

        if (!idToken) {
          sessionStorage.removeItem(SESSION_TOKEN_KEY);
          sessionStorage.removeItem(SESSION_USER_KEY);
          setIsLoading(false);
          return;
        }

        const claims        = parseJwtClaims(idToken);
        const refreshedUser = buildUserFromClaims(claims, idToken);
        setUser(refreshedUser);
        setIsLoading(false);
      } catch {
        sessionStorage.removeItem(SESSION_TOKEN_KEY);
        sessionStorage.removeItem(SESSION_USER_KEY);
        setIsLoading(false);
      }
    };

    void restoreSession();
  }, []);

  // Pulls the freshly-issued ID token, hydrates the user, and records the login.
  const finaliseSignIn = useCallback(async (): Promise<void> => {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    if (!idToken) throw new Error('Sign-in succeeded but no ID token was returned');

    const claims       = parseJwtClaims(idToken);
    const loggedInUser = buildUserFromClaims(claims, idToken);
    setUser(loggedInUser);
    api.logLogin().catch(() => { /* best-effort */ });
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      // v6 throws UserAlreadyAuthenticatedException if a stale session lingers
      // (e.g. tab restored after a partial logout). Clear it before signing in.
      try {
        await signOut();
      } catch {
        /* nothing to sign out of — expected on a cold login */
      }

      const { isSignedIn, nextStep } = await signIn({ username: email, password });

      if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        // Temp-password login — return a completer the UI calls with the new password
        return {
          type: 'new_password_required',
          complete: async (newPassword: string) => {
            const { isSignedIn: confirmed } = await confirmSignIn({
              challengeResponse: newPassword,
            });
            if (!confirmed) throw new Error('Password change did not complete sign-in');
            await finaliseSignIn();
          },
        };
      }

      if (!isSignedIn) {
        throw new Error(`Unsupported sign-in step: ${nextStep.signInStep}`);
      }

      await finaliseSignIn();
      return { type: 'success' };
    },
    [finaliseSignIn]
  );

  const logout = useCallback(() => {
    // Clear local state first so the UI cannot act on a stale session even if
    // the network calls below fail.
    sessionStorage.removeItem(SESSION_TOKEN_KEY);
    sessionStorage.removeItem(SESSION_USER_KEY);

    void signOut()
      .catch(() => { /* best-effort — local state is already cleared */ })
      .then(() => clearSessionCookie())
      .finally(() => {
        setUser(null);
        window.location.href = '/login';
      });
  }, []);

  const isRole = useCallback(
    (role: User['role']): boolean => user?.role === role,
    [user]
  );

  const value = useMemo<AuthContextValue>(
    () => ({ user, isLoading, login, logout, isRole }),
    [user, isLoading, login, logout, isRole]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export default AuthContext;
